// 知识点标签 content_key 计算：题目内容指纹
// 试卷编辑保存会删除重建 questions（ID 全换），标注不能依赖 question.id，
// 改用 question_type | 归一化题干前300字符 | score 作为稳定指纹（同步、可逆、便于排查）。

import { supabase } from '../supabase/client';

export interface TagDef {
  id: string;
  name: string;
  color?: string;
  category?: string;
  /** 编程语言归属：scratch | python | cpp */
  language?: string;
  /** 分类内手动排序（知识点标签管理页拖拽产生） */
  sort_order?: number;
}

export interface TagModeConfig {
  tags: TagDef[];
  // contentKey -> 已标注 tagId 列表
  marks: Record<string, string[]>;
  onToggleTag?: (contentKey: string, tagId: string) => void;
  // 后台管理预览：点击胶囊上的 × 删除按钮后回调（学员端只读场景不传）
  onRemoveTag?: (contentKey: string, tagId: string) => void;
  // 只读模式（学员查看等），仅展示已标注标签
  readOnly?: boolean;
  manageUrl?: string; // 标签管理页地址（空标签库时提示跳转）
}

/** 去除 HTML 标签与全部空白，取前 300 字符 */
export function normalizeQuestionContent(raw: string): string {
  return String(raw || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, '')
    .slice(0, 300);
}

/** 题目内容指纹（同步） */
export function computeContentKey(input: { type?: string; content?: string; title?: string; score?: number }): string {
  const normalized = normalizeQuestionContent(input.content || input.title || '');
  return `${input.type || ''}|${normalized}|${input.score ?? 0}`;
}

// ============ 学员端复盘：只读加载试卷的知识点标签与标注 ============

export interface PaperTagData {
  tags: TagDef[];
  marks: Record<string, string[]>;
}

// papers.category_type 历史数据存在中文别名（图形化编程/Python编程/E2图形化（*）等），统一归一化
const PAPER_LANG_MAP: Record<string, string> = {
  scratch: 'scratch', '图形化编程': 'scratch', 'E2图形化（小低组）': 'scratch', 'E2图形化（小高组）': 'scratch', 'E2图形化（初中组）': 'scratch',
  python: 'python', 'Python编程': 'python',
  cpp: 'cpp', 'C语言编程': 'cpp', 'C++': 'cpp',
};

/** 识别试卷的编程语言归属；识别不了时返回 ''（此时展示全部标签） */
export function resolvePaperLanguage(categoryType: string, title: string): string {
  const lang = PAPER_LANG_MAP[(categoryType || '').trim()];
  if (lang) return lang;
  // 兜底规则：试卷名称中出现"图形化"或"scratch"字样，一律判定为图形化编程
  if (/图形化|scratch/i.test(title || '')) return 'scratch';
  return '';
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// knowledge_points 标签库全量查询的 in-flight 去重 + 短缓存：
// 多张试卷并发加载标签时会重复发出同一查询（N 张试卷 = N 次），触发后端 429 限流。
// 语义不变：返回的数据与单次查询完全一致，仅合并重复请求。
let knowledgePointsInflight: Promise<TagDef[]> | null = null;
let knowledgePointsCache: { data: TagDef[]; at: number } | null = null;
const KNOWLEDGE_POINTS_TTL = 5 * 60 * 1000; // 5 分钟缓存，标签库属低频变更数据

/** 标签库全量查询（带 5 分钟缓存与并发去重；知识点组卷等后台场景直接复用） */
export function fetchKnowledgePoints(): Promise<TagDef[]> {
  if (knowledgePointsCache && Date.now() - knowledgePointsCache.at < KNOWLEDGE_POINTS_TTL) {
    return Promise.resolve(knowledgePointsCache.data);
  }
  if (knowledgePointsInflight) return knowledgePointsInflight;
  knowledgePointsInflight = supabase
    .from('knowledge_points')
    .select('id,name,color,category,language,sort_order')
    .order('sort_order', { ascending: true })
    .then(({ data }: any) => {
      const tags = (data || []) as TagDef[];
      knowledgePointsCache = { data: tags, at: Date.now() };
      return tags;
    })
    .finally(() => { knowledgePointsInflight = null; });
  return knowledgePointsInflight;
}

// ============ 标签排序：严格按知识点标签管理页的实时顺序 ============

export interface CategoryOrderRow {
  id: string;
  language: string;
  name: string;
  color: string;
  sort_order: number;
}

let categoriesInflight: Promise<CategoryOrderRow[]> | null = null;
let categoriesCache: { data: CategoryOrderRow[]; at: number } | null = null;

/** 加载一级分类表（含拖拽 sort_order），带 5 分钟缓存 */
export function fetchKnowledgeCategories(): Promise<CategoryOrderRow[]> {
  if (categoriesCache && Date.now() - categoriesCache.at < KNOWLEDGE_POINTS_TTL) {
    return Promise.resolve(categoriesCache.data);
  }
  if (categoriesInflight) return categoriesInflight;
  categoriesInflight = supabase
    .from('knowledge_categories')
    .select('id,language,name,color,sort_order')
    .then(({ data }: any) => {
      const cats = (data || []) as CategoryOrderRow[];
      categoriesCache = { data: cats, at: Date.now() };
      return cats;
    })
    .finally(() => { categoriesInflight = null; });
  return categoriesInflight;
}

/**
 * 按知识点标签管理页的实时顺序排序标签：
 * 一级分类按 knowledge_categories.sort_order（未设置的分类排在其后按名称），「未分类」固定末尾；
 * 分类内二级标签按 knowledge_points.sort_order（拖拽产生），再按名称兜底。
 */
export function sortTagsByManagerOrder(tags: TagDef[], categories: CategoryOrderRow[]): TagDef[] {
  const catOrder = (t: TagDef): [number, string] => {
    const cat = t.category || '未分类';
    if (cat === '未分类') return [Number.MAX_SAFE_INTEGER, ''];
    const c = categories.find(c => c.language === t.language && c.name === cat);
    if (c && c.sort_order > 0) return [c.sort_order, ''];
    return [Number.MAX_SAFE_INTEGER - 1, cat];
  };
  return [...tags].sort((a, b) => {
    const [oa, na] = catOrder(a);
    const [ob, nb] = catOrder(b);
    if (oa !== ob) return oa - ob;
    if (na !== nb) return na.localeCompare(nb, 'zh-Hans-CN');
    const sa = a.sort_order ?? 0, sb = b.sort_order ?? 0;
    if (sa !== sb) return sa - sb;
    return a.name.localeCompare(b.name, 'zh-Hans-CN');
  });
}

/**
 * 加载某试卷的知识点标签与已标注数据（学员端复盘/错题详情只读展示用）。
 * 仅真实试卷 UUID 可查（自定义组卷 custom-* / 虚拟试卷无标注数据，直接返回空）。
 * categoryType/title 已知时优先使用，避免额外查询 papers 表。
 */
export async function loadKnowledgeTagData(
  paperId: string,
  fallback?: { categoryType?: string; title?: string }
): Promise<PaperTagData> {
  const empty: PaperTagData = { tags: [], marks: {} };
  if (!paperId || !UUID_RE.test(paperId)) return empty;
  try {
    const [paperRes, allTags, catsRes, marksRes] = await Promise.all([
      supabase.from('papers').select('category_type,title').eq('id', paperId).maybeSingle(),
      fetchKnowledgePoints(),
      fetchKnowledgeCategories(),
      supabase.from('paper_question_tag_marks').select('content_key,tag_id').eq('paper_id', paperId),
    ]);
    if (allTags.length === 0) return empty;
    const categoryType = fallback?.categoryType || (paperRes.data as any)?.category_type || '';
    const title = fallback?.title || (paperRes.data as any)?.title || '';
    const lang = resolvePaperLanguage(categoryType, title);
    // 严格按试卷语言过滤标签；语言识别不了时展示全部（与后台预览逻辑一致）
    const tags = sortTagsByManagerOrder(
      lang ? allTags.filter(t => t.language === lang) : allTags,
      catsRes
    );
    if (tags.length === 0) return empty;
    const marks: Record<string, string[]> = {};
    (marksRes.data || []).forEach((m: { content_key: string; tag_id: string }) => {
      (marks[m.content_key] = marks[m.content_key] || []).push(m.tag_id);
    });
    return { tags, marks };
  } catch (e: any) {
    console.warn('[knowledge-tag] 加载试卷标签数据失败:', e?.message);
    return empty;
  }
}

// ============ 批量加载工具：有界并发 ============

/**
 * 有界并发批量加载多张试卷的标签数据（每批 3 张试卷并发）。
 * 语义与逐张 Promise.all 加载完全一致，仅限制同一时刻的请求峰值，
 * 避免记录页/错题页一次发出几十个请求触发后端 429 限流。
 */
export async function loadKnowledgeTagDataBatched(
  paperIds: string[],
  fallback?: { categoryType?: string; title?: string }
): Promise<PaperTagData[]> {
  const CONCURRENCY = 3;
  const results: PaperTagData[] = new Array(paperIds.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(CONCURRENCY, paperIds.length) }, async () => {
    while (cursor < paperIds.length) {
      const idx = cursor++;
      results[idx] = await loadKnowledgeTagData(paperIds[idx], fallback);
    }
  });
  await Promise.all(workers);
  return results;
}

// ============ 自由组卷：按知识点标签专项筛选 ============

/**
 * 查询一批试卷中标注了目标知识点标签的题目内容指纹集合。
 * tagIds 为空时返回 null（表示不做专项过滤）。
 * paper_id 是 text 类型，直接 in 查询即可。
 */
export async function loadTaggedContentKeys(paperIds: string[], tagIds: string[]): Promise<Set<string> | null> {
  if (!tagIds.length || !paperIds.length) return null;
  const keys = new Set<string>();
  const BATCH = 50;
  for (let i = 0; i < paperIds.length; i += BATCH) {
    const batch = paperIds.slice(i, i + BATCH);
    const { data, error } = await supabase
      .from('paper_question_tag_marks')
      .select('content_key,tag_id')
      .in('paper_id', batch)
      .in('tag_id', tagIds);
    if (error) throw new Error(`查询知识点标注失败: ${error.message}`);
    (data || []).forEach((m: { content_key: string }) => keys.add(m.content_key));
  }
  return keys;
}

/** 按标签分组查询内容指纹集合：每个 tagId 对应各自标注的 content_key 集合（用于「每标签各抽 N 道」的专项练习）
 *  ⚠️ 实现方式：逐标签用 tag_id=eq.X 小查询（URL 极短），再在客户端按 paperIds 集合过滤——
 *  曾用 paper_id=in.(50个UUID)+tag_id=in.(N个UUID) 组合长 URL 查询，标签选多时网关直接 502。 */
export async function loadTaggedContentKeysPerTag(
  paperIds: string[],
  tagIds: string[]
): Promise<Map<string, Set<string>> | null> {
  if (!tagIds.length || !paperIds.length) return null;
  const paperIdSet = new Set(paperIds);
  const result = new Map<string, Set<string>>();
  console.log('[questionTag] loadTaggedContentKeysPerTag: 标签数', tagIds.length, '试卷数', paperIds.length);
  for (const tagId of tagIds) {
    const keys = new Set<string>();
    // 分页拉取该标签的全部标注（单标签标注量一般 <1000，一页基本够；超出则继续翻页）
    let from = 0;
    const PAGE = 1000;
    for (;;) {
      const { data, error } = await supabase
        .from('paper_question_tag_marks')
        .select('content_key,paper_id')
        .eq('tag_id', tagId)
        .range(from, from + PAGE - 1);
      if (error) throw new Error(`查询知识点标注失败: ${error.message}`);
      (data || []).forEach((m: { content_key: string; paper_id: string }) => {
        if (paperIdSet.has(m.paper_id)) keys.add(m.content_key);
      });
      if (!data || data.length < PAGE) break;
      from += PAGE;
    }
    result.set(tagId, keys);
  }
  return result;
}

/** 按允许的内容指纹集合过滤题目池（allowed 为 null 时原样返回） */
export function filterQuestionsByContentKeys(
  questions: Array<{ question_type?: string | null; type?: string | null; content?: string | null; title?: string | null; score?: number | null }>,
  allowed: Set<string> | null
): typeof questions {
  if (!allowed) return questions;
  return questions.filter(q => allowed.has(
    computeContentKey({ type: q.question_type || q.type, content: q.content, title: (q as any).title, score: q.score ?? undefined })
  ));
}

// ============ 组卷携带知识点标注（2026-10）============
// 自定义/知识点组卷抽出的题目来自源试卷，源题在 paper_question_tag_marks 里有标注，
// 但新试卷（后台草稿新 paper_id / 学员 custom-* 虚拟 ID）没有 marks 行，导致编辑器显示"暂无标注"。
// 方案：抽题时按源试卷查询标注 → 以内容指纹为桥迁移（后台落库 / 学员端写入题目快照随 exam_records 持久化）。

/**
 * 按题目源试卷（paper_id）查询其已有的知识点标注，返回与这些题目内容指纹匹配的 marks。
 * questions 需携带 paper_id 与内容指纹所需字段（question_type/type、content/title、score）。
 */
export async function loadTagMarksForQuestions(
  questions: Array<{ paper_id?: string | null; question_type?: string | null; type?: string | null; content?: string | null; title?: string | null; score?: number | null }>
): Promise<Record<string, string[]>> {
  const paperIds = Array.from(new Set(
    questions.map(q => q.paper_id).filter(Boolean) as string[]
  ));
  if (!paperIds.length) return {};
  const byContentKey: Record<string, string[]> = {};
  const BATCH = 50;
  for (let i = 0; i < paperIds.length; i += BATCH) {
    const batch = paperIds.slice(i, i + BATCH);
    let from = 0;
    const PAGE = 1000;
    for (;;) {
      const { data, error } = await supabase
        .from('paper_question_tag_marks')
        .select('content_key,tag_id')
        .in('paper_id', batch)
        .range(from, from + PAGE - 1);
      if (error) throw new Error(`查询知识点标注失败: ${error.message}`);
      (data || []).forEach((m: { content_key: string; tag_id: string }) => {
        (byContentKey[m.content_key] = byContentKey[m.content_key] || []).push(m.tag_id);
      });
      if (!data || data.length < PAGE) break;
      from += PAGE;
    }
  }
  if (Object.keys(byContentKey).length === 0) return {};
  // 只保留与给定题目指纹匹配的标注
  const wanted = new Set(questions.map(q => computeContentKey({
    type: q.question_type || q.type,
    content: q.content,
    title: (q as any).title,
    score: q.score ?? undefined,
  })));
  const marks: Record<string, string[]> = {};
  for (const key of Object.keys(byContentKey)) {
    if (wanted.has(key)) marks[key] = byContentKey[key];
  }
  return marks;
}

/** 由 marks 反查标签库，构建只读展示用的 PaperTagData（tags 按管理页顺序排序、语言按需过滤） */
export async function buildTagDataFromMarks(marks: Record<string, string[]>): Promise<PaperTagData> {
  const usedIds = new Set(Object.values(marks).flat());
  if (usedIds.size === 0) return { tags: [], marks: {} };
  const [allTags, cats] = await Promise.all([fetchKnowledgePoints(), fetchKnowledgeCategories()]);
  // 标注只涉及一种语言时严格按该语言过滤，否则展示全部（与 loadKnowledgeTagData 行为一致）
  const langs = new Set(allTags.filter(t => usedIds.has(t.id)).map(t => t.language).filter(Boolean));
  const pool = langs.size === 1 ? allTags.filter(t => t.language === [...langs][0]) : allTags;
  const tags = sortTagsByManagerOrder(pool, cats).filter(t => usedIds.has(t.id));
  return { tags, marks };
}

/** 从题目快照（questions[].knowledgeTags，交卷时写入 exam_records）提取标签数据：自定义组卷复盘兜底 */
export function extractTagDataFromQuestions(questions: any[]): PaperTagData {
  const byId = new Map<string, TagDef>();
  const marks: Record<string, string[]> = {};
  for (const q of questions || []) {
    const kts = (q as any)?.knowledgeTags;
    if (!Array.isArray(kts) || kts.length === 0) continue;
    const contentKey = computeContentKey({
      type: (q as any).type ?? (q as any).question_type,
      content: (q as any).content,
      title: (q as any).title,
      score: (q as any).score,
    });
    const ids: string[] = [];
    kts.forEach((t: any) => {
      if (t?.id) { byId.set(t.id, t as TagDef); ids.push(t.id); }
    });
    if (ids.length > 0) (marks[contentKey] = marks[contentKey] || []).push(...ids);
  }
  return { tags: Array.from(byId.values()), marks };
}
